import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { MessageSquare, Activity, Send, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Reusable audit + comments panel for any entity (record, SOP, employee, etc.)
 * Usage: <ActivityPanel entityType="record" entityId={id} />
 */
export default function ActivityPanel({ entityType, entityId }) {
  const { user } = useAuth();
  const [activity, setActivity] = useState([]);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, c] = await Promise.all([
      api.get(`/activity?entity_type=${entityType}&entity_id=${entityId}&limit=30`),
      api.get(`/comments?entity_type=${entityType}&entity_id=${entityId}`),
    ]);
    setActivity(a.data);
    setComments(c.data);
  };

  useEffect(() => {
    if (entityType && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post("/comments", { entity_type: entityType, entity_id: entityId, content: text });
      setText("");
      load();
    } catch {
      toast.error("Comment failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/comments/${id}`);
    load();
  };

  return (
    <div className="border border-border bg-card" data-testid="activity-panel">
      <Tabs defaultValue="comments">
        <TabsList className="w-full rounded-none border-b border-border bg-transparent p-0 h-auto">
          <TabsTrigger value="comments" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-comments">
            <MessageSquare className="w-3.5 h-3.5 mr-2" /> Comments · {comments.length}
          </TabsTrigger>
          <TabsTrigger value="activity" className="flex-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent font-mono-plex uppercase text-xs tracking-widest" data-testid="tab-activity">
            <Activity className="w-3.5 h-3.5 mr-2" /> Activity · {activity.length}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="comments" className="p-4 space-y-4 m-0">
          {comments.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6 font-mono-plex uppercase tracking-widest">
              No comments yet
            </div>
          ) : (
            <ul className="space-y-4 max-h-80 overflow-auto">
              {comments.map((c) => (
                <li key={c.id} className="group flex gap-3" data-testid={`comment-${c.id}`}>
                  <div className="w-7 h-7 shrink-0 rounded-sm bg-primary text-primary-foreground grid place-items-center text-xs font-bold">
                    {c.user_name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium">{c.user_name}</span>
                      <span className="text-[10px] font-mono-plex text-muted-foreground uppercase tracking-widest">
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                      {c.user_id === user?.id && (
                        <button
                          onClick={() => del(c.id)}
                          className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`comment-delete-${c.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </button>
                      )}
                    </div>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{c.content}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={post} className="flex flex-col gap-2">
            <Textarea
              rows={2}
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Add a comment…"
              data-testid="comment-input"
              className="rounded-sm resize-none"
            />
            <Button
              type="submit" disabled={!text.trim() || busy}
              data-testid="comment-submit"
              className="rounded-sm bg-primary hover:bg-primary/90 self-end"
              size="sm"
            >
              <Send className="w-3.5 h-3.5 mr-2" /> Post
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="activity" className="p-4 m-0">
          {activity.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-6 font-mono-plex uppercase tracking-widest">
              No activity yet
            </div>
          ) : (
            <ol className="space-y-4 max-h-80 overflow-auto">
              {activity.map((a) => (
                <li key={a.id} className="border-l-2 border-border pl-3 relative" data-testid={`activity-${a.id}`}>
                  <div className="absolute -left-[5px] top-1 w-2 h-2 bg-primary rounded-full" />
                  <div className="text-xs font-mono-plex uppercase tracking-widest text-primary">{a.action}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {a.user_name} · {new Date(a.created_at).toLocaleString()}
                  </div>
                  {a.note && <div className="text-xs mt-1 italic text-foreground/80">&ldquo;{a.note}&rdquo;</div>}
                </li>
              ))}
            </ol>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
